import { ItemListProvider, useItemListContext } from '@/components/itemList/ItemListProvider';
import { SelectionBar } from '@/components/itemList/SelectionBar';
import { FileCard } from '@/components/itemList/FileCard';
import { FileRow } from '@/components/itemList/FileRow';
import { CreateButtons } from '@/components/itemList/CreateButtons';
import { ItemListModals } from '@/components/itemList/ItemListModals';
import { useSortedItems } from '@/hooks/useSortedItems';
import { useSession } from '@/hooks/useSession';
import { useAppSelector } from '@/store/hooks';
import type { ViewMode } from '@/hooks/useViewMode';
import type { NormalizedItem } from '@/components/itemList/types';

interface ProjectFileListProps {
  items: NormalizedItem[];
  sortCriteriaIndex: number;
  sortAscending: boolean;
  onRefresh: () => void;
  projectId?: string;
  viewMode?: ViewMode;
}

export function ProjectFileList({
  items,
  sortCriteriaIndex,
  sortAscending,
  onRefresh,
  projectId,
  viewMode = 'grid',
}: ProjectFileListProps) {
  const { updateIsQuerying } = useSession();
  const userRole = useAppSelector((state) => state.project.userRole) as string | null;
  const sortedItems = useSortedItems(items, sortCriteriaIndex, sortAscending);

  return (
    <ItemListProvider
      items={sortedItems}
      userRole={userRole}
      useCase="project"
      parentId={projectId}
      sortCriteria={sortCriteriaIndex}
      sortAscending={sortAscending}
      onRefresh={onRefresh}
      updateIsQuerying={updateIsQuerying}
    >
      <ProjectFileListContent sortCriteriaIndex={sortCriteriaIndex} sortAscending={sortAscending} viewMode={viewMode} />
    </ItemListProvider>
  );
}

function ProjectFileListContent({
  sortCriteriaIndex,
  sortAscending,
  viewMode,
}: {
  sortCriteriaIndex: number;
  sortAscending: boolean;
  viewMode: ViewMode;
}) {
  const { items: contextItems } = useItemListContext();
  const sortedItems = useSortedItems(contextItems, sortCriteriaIndex, sortAscending);

  return (
    <>
      <SelectionBar />
      {sortedItems.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">No files</div>
      ) : viewMode === 'grid' ? (
        <ul className="m-0 grid list-none gap-4 p-0 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
          {sortedItems.map((item) => (
            <li key={item.id}>
              <FileCard item={item} showPreviewToggle />
            </li>
          ))}
        </ul>
      ) : (
        <ul className="m-0 list-none space-y-1 p-0">
          {sortedItems.map((item) => (
            <FileRow key={item.id} item={item} variant="full" />
          ))}
        </ul>
      )}
      <CreateButtons showUpload showNewItem={false} />
      <ItemListModals />
    </>
  );
}
